/**
 * Printer reachability helpers, shared by the Devices page and the remote-print
 * failure modal so every "online / offline" badge is derived from the same two
 * signals that the backend actually uses to deliver a job:
 *
 *  - an **enrolled print agent** (a headless socket client on the printer's PC)
 *    that advertises the printer's share, or whose hostname / IP matches the
 *    host — the reliable signal, present even with no browser open; and
 *  - a **dashboard** on that host registered with the print gateway
 *    (`print:<host>` room) — the legacy signal, only present while a tab is open.
 */
import type { PrintAgentInfo } from '../services/httpServices/settingsService';

/**
 * Normalise a host/identity to a comparable lowercase string, the same way the
 * print gateway keys its rooms (trim, strip surrounding `\` or `/`, lowercase).
 */
export function normalizeHostKey(value: string): string {
  return (value || '')
    .trim()
    .replace(/^[\\/]+/, '')
    .replace(/[\\/]+$/, '')
    .toLowerCase();
}

/**
 * True when `agent` serves a printer identified by `host` and/or `shareName`.
 * The share name is the most specific key; the host (hostname or any advertised
 * IP) is the fallback when the share is unknown or the stored host is stale.
 */
export function agentMatchesPrinter(
  agent: PrintAgentInfo,
  host: string,
  shareName?: string,
): boolean {
  const share = (shareName || '').trim().toLowerCase();
  if (
    share &&
    (agent.printers || []).some(
      (p) => (p.shareName || '').trim().toLowerCase() === share,
    )
  ) {
    return true;
  }

  const hostKey = normalizeHostKey(host);
  if (!hostKey) return false;
  if (normalizeHostKey(agent.hostname || '') === hostKey) return true;
  return (agent.ipAddresses || []).some((ip) => normalizeHostKey(ip) === hostKey);
}

/**
 * Whether a printer on `host` (optionally identified by `shareName`) is
 * reachable right now. An enrolled, enabled agent that matches is authoritative;
 * otherwise a dashboard identity for that host counts (legacy fallback).
 */
export function isPrinterOnline(
  host: string,
  shareName: string | undefined,
  agents: PrintAgentInfo[],
  onlineHosts: string[],
): boolean {
  const agentOnline = agents.some(
    (agent) => agent.enabled && agent.online && agentMatchesPrinter(agent, host, shareName),
  );
  if (agentOnline) return true;

  const hostKey = normalizeHostKey(host);
  return Boolean(hostKey) && onlineHosts.includes(hostKey);
}
